'use client';

import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useDebounce } from 'use-debounce';
import { useAuth } from 'src/context/auth';
import useGetMedTerritory from 'src/hooks/medscoutmap/useGetMedTerritory';
import { useUpdateCurrentUserMutation } from 'src/api/redux';
import { setCurrentTerritory } from 'src/redux/slices/discoveryMapSlice';

/**
 * Initializes the Discovery map's current territory from the user's
 * `last_territory` setting. Fetches territory details and dispatches
 * to the discoveryMap Redux slice. Handles 404 errors by clearing
 * the territory.
 */
const useDiscoveryTerritory = () => {
  const dispatch = useDispatch();
  const { user, setUser } = useAuth();
  const [updateCurrentUser] = useUpdateCurrentUserMutation();
  const lastTerritory = user?.last_territory;
  const isWithoutTerritory =
    lastTerritory?.value === 'USE_MAP_NO_TERRITORY' || isNaN(lastTerritory?.id);
  const territoryId = isWithoutTerritory
    ? null
    : lastTerritory?.id || user?.assigned_territory;
  const shouldFetch = territoryId && !isNaN(territoryId);
  const { data: territory, error: territoryFetchError } = useGetMedTerritory(
    shouldFetch ? territoryId : null
  );

  // Debounce territory changes to prevent rapid updates during impersonation
  const [debouncedTerritory] = useDebounce(territory, 150);
  const [debouncedIsWithoutTerritory] = useDebounce(isWithoutTerritory, 150);
  const [debouncedTerritoryError] = useDebounce(territoryFetchError, 150);

  useEffect(() => {
    if (debouncedIsWithoutTerritory) {
      dispatch(setCurrentTerritory(null));
      return;
    }
    if (!debouncedTerritory) return;
    dispatch(setCurrentTerritory(debouncedTerritory));
  }, [debouncedTerritory, debouncedIsWithoutTerritory, dispatch]);

  useEffect(() => {
    const errorStatus = (debouncedTerritoryError as { status?: number })
      ?.status;
    if (!debouncedTerritoryError || !shouldFetch) return;

    if (errorStatus === 404) {
      // Territory no longer exists — clear locally and on server
      dispatch(setCurrentTerritory(null));
      setUser((oldUser) => ({ ...oldUser, last_territory: null }));
      updateCurrentUser({ last_territory_id: null }).catch(() => {});
    } else if (errorStatus === 403) {
      // User doesn't have access (e.g. after stopping impersonation) — clear locally only
      dispatch(setCurrentTerritory(null));
    }
  }, [debouncedTerritoryError, shouldFetch, setUser, updateCurrentUser, dispatch]);
};

export default useDiscoveryTerritory;
